import React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Typography,
} from "@mui/material";

import {
  CalendarSlot,
  Meeting,
  MeetingStatus,
  Organisation,
  PatientSummary,
  User,
} from "../../../types";
import { useGetMeetingsWithIds } from "../../../store";
import AdminMeetingsTableRow from "./admin-meetings-table-row";

type AdminMeetingsTableProps = {
  meetingsLoading: boolean;
  usersLoading: boolean;
  teamsLoading: boolean;
  clientsLoading: boolean;
  patientsLoading: boolean;
  filteredMeetings: CalendarSlot[];
  rowsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  sortBy: string;
  sortOrder: "asc" | "desc";
  onSort: (field: string) => void;
  searchTerm?: string;
  cardFilter?: MeetingStatus | null;
  startDate?: Date | null;
  endDate?: Date | null;
  userDictionary: Record<string, User>;
  teamDictionary: Record<string, Organisation>;
  patientDictionary: Record<string, PatientSummary>;
  selectedClientId: string;
};

const AdminMeetingsTable: React.FC<AdminMeetingsTableProps> = ({
  meetingsLoading,
  usersLoading,
  teamsLoading,
  clientsLoading,
  patientsLoading,
  filteredMeetings,
  rowsPerPage,
  currentPage,
  onPageChange,
  sortBy,
  sortOrder,
  onSort,
  searchTerm,
  cardFilter,
  startDate,
  endDate,
  userDictionary,
  teamDictionary,
  patientDictionary,
  selectedClientId,
}) => {
  const paginatedMeetings = filteredMeetings.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage,
  );

  const paginatedMeetingIds = paginatedMeetings.map((meeting) => meeting.meetingId);

  const { data: fullMeetings, isLoading: fullMeetingsLoading } =
    useGetMeetingsWithIds(paginatedMeetingIds, selectedClientId);

  const fullMeetingDictionary = React.useMemo(() => {
    const dictionary: Record<string, Meeting> = {};

    for (const meeting of fullMeetings || []) {
      if (meeting) {
        dictionary[meeting.meetingId] = meeting;
      }
    }

    return dictionary;
  }, [fullMeetings]);

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="center" sx={{ py: 0.5, minWidth: 80 }}>
              <TableSortLabel
                active={sortBy === "status"}
                direction={sortBy === "status" ? sortOrder : "asc"}
                onClick={() => onSort("status")}
                sx={{ fontSize: "0.875rem" }}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5, minWidth: 115 }}>
              <TableSortLabel
                active={sortBy === "startTime"}
                direction={sortBy === "startTime" ? sortOrder : "asc"}
                onClick={() => onSort("startTime")}
                sx={{ fontSize: "0.875rem" }}
              >
                Start Time
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ py: 0.5, minWidth: 115 }}>
              <TableSortLabel
                active={sortBy === "endTime"}
                direction={sortBy === "endTime" ? sortOrder : "asc"}
                onClick={() => onSort("endTime")}
                sx={{ fontSize: "0.875rem" }}
              >
                End Time
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ py: 0.5 }}>
              <TableSortLabel
                active={sortBy === "creatorId"}
                direction={sortBy === "creatorId" ? sortOrder : "asc"}
                onClick={() => onSort("creatorId")}
                sx={{ fontSize: "0.875rem" }}
              >
                Created by
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ py: 0.5, width: 80 }}>
              <TableSortLabel
                active={sortBy === "patientId"}
                direction={sortBy === "patientId" ? sortOrder : "asc"}
                onClick={() => onSort("patientId")}
                sx={{ fontSize: "0.875rem" }}
              >
                Patient
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ py: 0.5, fontSize: "0.875rem" }}>
              Attendees
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {meetingsLoading ||
            usersLoading ||
            teamsLoading ||
            clientsLoading ||
            patientsLoading ||
            fullMeetingsLoading ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography>Loading...</Typography>
              </TableCell>
            </TableRow>
          ) : filteredMeetings.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography color="text.secondary">
                  {searchTerm || cardFilter !== null || startDate || endDate
                    ? "No meetings match your filters"
                    : "No meetings found for the selected client"}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedMeetings.map((meeting, index) => {
              const fullMeeting = fullMeetingDictionary[meeting.meetingId];

              if (!fullMeeting) {
                return null;
              }

              return (
                <React.Fragment key={`${meeting.meetingId}${index}`}>
                  <AdminMeetingsTableRow
                    meeting={fullMeeting}
                    userDictionary={userDictionary}
                    teamDictionary={teamDictionary}
                    patientDictionary={patientDictionary}
                    selectedClientId={selectedClientId}
                  />
                </React.Fragment>
              );
            })
          )}
        </TableBody>
      </Table>
      <TablePagination
        component="div"
        count={filteredMeetings.length}
        page={currentPage}
        onPageChange={(_, nextPage) => onPageChange(nextPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[rowsPerPage]}
      />
    </TableContainer>
  );
};

export default AdminMeetingsTable;
